import {Chart} from "primereact/chart";
import {SelectButton} from "primereact/selectbutton";
import {useEffect, useMemo} from "react";

import {SnapshotType} from "@reva/shared/valuation-data";
import type {ChartData, ChartDataset, ChartOptions} from "chart.js";

import type {SnapshotStatisticsInterval, SnapshotStatisticsPeriod} from "../../api/payload-types/snapshot-search";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {
    SNAPSHOT_STATISTICS_INTERVALS,
    type SnapshotScope,
    selectCurrentStatistics,
    selectStatisticsError,
    selectStatisticsInterval,
    selectStatisticsLoading,
    selectStatisticsTotals,
} from "../../redux/searchSnapshotsSlice";
import {changeStatisticsIntervalThunk, initializeSnapshotStatisticsThunk} from "../../redux/searchSnapshotsThunk";
import {selectDarkTheme} from "../../redux/themeSlice";
import {getChartStyle, getSnapshotTypeColors} from "../../util/chartUtils";
import {capitalize} from "../../util/stringUtil";
import {buildStatisticsChartOptions, LegendItem, STATISTICS_CHART_BOX_CLASS, StatTile} from "./startPageWidgets";

/** Date format of the x-axis labels per statistics interval. */
const PERIOD_LABEL_FORMAT: Record<SnapshotStatisticsInterval, Intl.DateTimeFormatOptions> = {
    day: {day: "2-digit", month: "2-digit"},
    week: {day: "2-digit", month: "2-digit"},
    month: {month: "2-digit", year: "2-digit"},
    year: {year: "numeric"},
};

interface SnapshotStatisticsPanelProps {
    className?: string;
    /** Which statistics this panel instance shows: "own" (DONE tab) or "all" (Admin statistics sub-tab). */
    scope: SnapshotScope;
    /**
     * Optional: nur diese Snapshot-Typen anzeigen (der Typ-Spalten-Filter der Historien-Tabelle wirkt
     * damit auch auf Grafik, Kacheln und Legende). Fehlt = alle Typen.
     */
    visibleTypes?: SnapshotType[];
}

/**
 * #19113: Activity statistics above the DONE tab's snapshot list — snapshot counts per calendar
 * bucket as a grouped bar chart (pre-finalizations vs. finalizations), switchable between
 * day/week/month/year buckets, plus summary tiles (all-time totals and the average per bucket
 * since the user's first snapshot). Tiles and chart series appear only for snapshot types the
 * user has ever produced; users without any snapshot get no statistics block at all.
 */
export default function SnapshotStatisticsPanel({className, scope, visibleTypes}: SnapshotStatisticsPanelProps) {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();

    // Gemountet wird das Panel erst bei Bedarf (Statistik-Umschalter der Historie bzw. Admin-Reiter
    // „Statistiken“); der Thunk lädt nur bei Cache-Miss des gewählten Intervalls.
    useEffect(() => {
        void dispatch(initializeSnapshotStatisticsThunk(scope));
    }, [dispatch, scope]);

    const isDarkTheme = useRevaSelector(selectDarkTheme);
    // Per-Scope-State (wie die Bewertungs-Tabs): das Panel liest ausschließlich die Statistiken
    // seines eigenen Scopes — es kann nie die Zahlen des anderen Scopes anzeigen.
    const interval = useRevaSelector((state) => selectStatisticsInterval(state, scope));
    const statistics = useRevaSelector((state) => selectCurrentStatistics(state, scope));
    const totals = useRevaSelector((state) => selectStatisticsTotals(state, scope));
    const loading = useRevaSelector((state) => selectStatisticsLoading(state, scope));
    const statisticsError = useRevaSelector((state) => selectStatisticsError(state, scope));

    const periods = statistics?.periods ?? null;
    const ready = !loading && !!periods;

    // Show only what the user has ever produced: no tile/series for a snapshot type the user never
    // created, and (below) no statistics block at all for users without any snapshot.
    const {totalPreFinalCount = 0, totalFinalCount = 0} = totals ?? {};
    const typeVisible = (type: SnapshotType) => !visibleTypes || visibleTypes.includes(type);
    const showFinal = totalFinalCount > 0 && typeVisible(SnapshotType.Final);
    const showPreFinal = totalPreFinalCount > 0 && typeVisible(SnapshotType.PreFinal);
    const hasSnapshots = showFinal || showPreFinal;

    const chartStyle = useMemo(() => getChartStyle(isDarkTheme), [isDarkTheme]);
    // Serienfarben aus der App-Palette, zentral aufgelöst (siehe getSnapshotTypeColors) — dieselbe
    // Quelle wie die Swatches der Historie-Typ-Filter, damit Balken und Checkboxen übereinstimmen.
    const {colorFinal, colorPreFinal} = getSnapshotTypeColors(isDarkTheme);

    const intervalOptions = SNAPSHOT_STATISTICS_INTERVALS.map((value) => ({
        value,
        label: t(`start.snapshots.statistics.interval${capitalize(value)}`),
    }));

    // Memoized so the chart component's memo sees stable identities — otherwise every unrelated
    // re-render of the tab would destroy and rebuild the chart.js instance
    const chartData = useMemo<ChartData<"bar">>(() => {
        const labels = (periods ?? []).map((p) =>
            new Date(p.periodStart).toLocaleDateString(language, PERIOD_LABEL_FORMAT[interval]),
        );
        const buildDataset = (
            label: string,
            color: string,
            values: (p: SnapshotStatisticsPeriod) => number,
        ): ChartDataset<"bar"> => ({
            label,
            backgroundColor: color,
            borderRadius: 4,
            maxBarThickness: 24,
            data: (periods ?? []).map(values),
        });
        const datasets: ChartDataset<"bar">[] = [];
        if (showFinal) {
            datasets.push(buildDataset(t("start.snapshots.statistics.seriesFinal"), colorFinal, (p) => p.finalCount));
        }
        if (showPreFinal) {
            datasets.push(
                buildDataset(t("start.snapshots.statistics.seriesPreFinal"), colorPreFinal, (p) => p.preFinalCount),
            );
        }
        return {labels, datasets};
    }, [periods, interval, showFinal, showPreFinal, colorFinal, colorPreFinal, t, language]);

    // Historie: x-Labels bei Platzmangel ausdünnen (autoSkip), es können viele Kalender-Buckets sein.
    const chartOptions = useMemo<ChartOptions<"bar">>(
        () => buildStatisticsChartOptions(chartStyle, true),
        [chartStyle],
    );

    // Ø since the user's first snapshot — the bucket count comes from the backend (same calendar
    // and time zone as the chart buckets); old backends without the field fall back to the window
    // length and get no hint line, since the "since your first valuation" claim wouldn't hold
    const periodsSinceFirst = statistics?.periodsSinceFirst ?? 0;
    const averageDivisor = periodsSinceFirst > 0 ? periodsSinceFirst : periods?.length || 1;
    const formatAverage = (total: number) =>
        (total / averageDivisor).toLocaleString(language, {minimumFractionDigits: 0, maximumFractionDigits: 1});
    const intervalLabel = t(`start.snapshots.statistics.interval${capitalize(interval)}`);
    const averageLabel = t("start.snapshots.statistics.average", {interval: intervalLabel});
    const averageHint =
        periodsSinceFirst > 0
            ? t(`start.snapshots.statistics.averageHint${capitalize(interval)}`, {count: periodsSinceFirst})
            : undefined;

    // Users without any snapshot get no statistics block at all (errors are still surfaced)
    if (!loading && !statisticsError && !hasSnapshots) {
        return null;
    }

    return (
        <div className={className}>
            <ErrorIndicator titleI18nKey="start.snapshots.statistics.queryError" errorObject={statisticsError} />
            {/* One row: summary tiles on the left, interval switcher floating right at tile height */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex flex-wrap gap-3">
                    {loading && <LoadingIndicator loading={true} />}
                    {ready && (
                        <>
                            {/* One tile pair per snapshot type the user ever produced: all-time
                                total plus the average per bucket since the first valuation */}
                            {showFinal && (
                                <div className="flex gap-1">
                                    <StatTile
                                        value={String(totalFinalCount)}
                                        label={t("start.snapshots.statistics.totalFinal")}
                                    />
                                    <StatTile
                                        value={formatAverage(totalFinalCount)}
                                        label={averageLabel}
                                        hint={averageHint}
                                    />
                                </div>
                            )}
                            {showPreFinal && (
                                <div className="flex gap-1">
                                    <StatTile
                                        value={String(totalPreFinalCount)}
                                        label={t("start.snapshots.statistics.totalPreFinal")}
                                    />
                                    <StatTile
                                        value={formatAverage(totalPreFinalCount)}
                                        label={averageLabel}
                                        hint={averageHint}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
                {/* Right column: interval switcher with the chart legend stacked directly beneath it —
                    together as tall as the summary tiles, so the legend costs no vertical space */}
                <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-2">
                        <SelectButton
                            value={interval}
                            options={intervalOptions}
                            allowEmpty={false}
                            onChange={(e) => void dispatch(changeStatisticsIntervalThunk(scope, e.value))}
                        />
                    </div>
                    {ready && (
                        <div className="flex gap-4">
                            {showFinal && (
                                <LegendItem color={colorFinal} label={t("start.snapshots.statistics.seriesFinal")} />
                            )}
                            {showPreFinal && (
                                <LegendItem
                                    color={colorPreFinal}
                                    label={t("start.snapshots.statistics.seriesPreFinal")}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
            {ready && (
                <div className={STATISTICS_CHART_BOX_CLASS}>
                    <Chart type="bar" className="w-full h-full" data={chartData} options={chartOptions} />
                </div>
            )}
        </div>
    );
}
